import { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import Link from '@atlaskit/link';
import { CustomerServiceManagementIcon, JiraServiceManagementIcon, RovoIcon } from '@atlaskit/logo';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { JSMOverlayTemplate } from '@/components/signup/layouts/product-overlay-template';
import { getIdentityBaseUrl } from '@/components/signup/utils/environment';
import { isJsmPlaygroundSignup } from '@/components/signup/utils/jsm-playground-signup-intent';
import { ServiceCollectionLogoV2 } from '@/components/signup/utils/logos/service-collection-logo-v2';
import { ProductKey } from '@/components/signup/utils/products';
import { wacToken } from '@/libraries/design/foundations';
import { DataFoundationsWrapper } from '@/utils/data-foundations/data-foundation-types';
import { experiments } from '@/utils/experiment/manifest';
import { getExperiment } from '@/utils/experiment/use-experiment';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import ErrorBoundary from '../../components/percieved-provisioning/error-boundary/error-boundary';
import { useCloudStatus } from '../../hooks/use-cloud-status';
import { useConfirmationPage } from '../../hooks/use-confirmation-page';
import { useProvisioningLatencyUfo } from '../../hooks/use-provisioning-latency-ufo';
import { createProductUrl, isValidOnDemandUrl } from '../../utils/create-product-url';
import { JSM_TEMPLATE_ID_TO_BLUEPRINT_KEY } from '../jsm-use-case-template-choice/jsm-use-case-template-choice-constants';
import { ServiceCollectionProvisioningAppIcons } from '../service-collection-provisioning-screen/components/service-collection-provisioning-app-icons';

import { getJsmIncidentManagementFeedback } from './get-jsm-incident-management-feedback';
import { JsmUseCaseProvisioningGrid } from './jsm-use-case-provisioning-grid';
import { jsmUseCaseProvisioningMessages } from './jsm-use-case-provisioning-messages';
import {
	JSM_USE_CASE_PROVISIONING_MAX_PENDING_PROGRESS,
	JSM_USE_CASE_PROVISIONING_PROGRESS_DURATION_MS,
	JsmUseCaseProvisioningProgressBar,
	LEGACY_JSM_USE_CASE_PROVISIONING_PROGRESS_DURATION_MS,
	type JsmUseCaseProvisioningProgressStatus,
	useJsmUseCaseProvisioningProgress,
} from './jsm-use-case-provisioning-progress-bar';
import { useJsmIncidentManagementSetup } from './use-jsm-incident-management-setup';
import { useJsmUseCaseProjectCreate } from './use-jsm-use-case-project-create';

const SUCCESS_REDIRECT_DELAY_MS = 1_000;
const PROGRESS_MESSAGE_STEP_COUNT = 4;
const PROGRESS_MESSAGE_STEP_SIZE =
	JSM_USE_CASE_PROVISIONING_MAX_PENDING_PROGRESS / PROGRESS_MESSAGE_STEP_COUNT;

export const JsmUseCaseProvisioningScreen = () => {
	const intl = useIntl();
	const prefersReducedMotion = usePrefersReducedMotion();
	const { onDemandUrl } = useSearchParameters();
	const { cloudId, isProduction, createCloudStatus } = useCloudStatus();
	const { startProductSetupSystem, completeProductSetupSystem } = useProvisioningLatencyUfo();
	useEffect(() => {
		if (createCloudStatus === 'success') {
			startProductSetupSystem({
				productKey: ProductKey.SERVICE_COLLECTION,
				provisioningFlow: 'regular',
			});
		}
	}, [createCloudStatus, startProductSetupSystem]);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isCsmAppificationEnabled = checkFeatureFlagGate(
		featureFlags.csmAppificationStandaloneNavigation,
	);
	const {
		userSegmentationContext: { selectedTeamType, selectedTemplateId, incidentManagementIntent },
	} = useConfirmationPage();
	const hasFiredRedirectAnalyticsRef = useRef(false);
	let gatedIncidentIntent: typeof incidentManagementIntent;
	try {
		const experiment = { value: false, ...getExperiment(experiments.operationsLandFlow) };
		if (experiment.value === true) {
			gatedIncidentIntent = incidentManagementIntent;
		}
	} catch {
		// An unavailable experiment keeps the existing project-only flow.
	}
	const isIncidentManagement = Boolean(gatedIncidentIntent);
	const isReliableProvisioningEnabled =
		isIncidentManagement ||
		isJsmPlaygroundSignup(window.location.search) ||
		checkFeatureFlagGate(featureFlags.jsmLandUseCaseOnboardingBugfix);

	const { projectErrorMessage, projectFailure, projectKey, projectLifecycle, projectStatus } =
		useJsmUseCaseProjectCreate({
			teamType: selectedTeamType,
			templateId: selectedTemplateId,
			requireReliableProvisioning: isIncidentManagement,
		});
	const incidentSetup = useJsmIncidentManagementSetup({
		intent: gatedIncidentIntent,
		templateId: selectedTemplateId,
		projectKey,
		projectStatus,
	});
	const hasProjectError = ['error', 'timeout'].includes(projectStatus);
	const provisioningStatus =
		isIncidentManagement && projectStatus === 'complete' ? incidentSetup.status : projectStatus;
	const hasError = ['error', 'timeout'].includes(provisioningStatus);
	const isComplete = provisioningStatus === 'complete';
	const shouldAnimateArtwork = !isComplete && !hasError && !prefersReducedMotion;
	const templateKey = selectedTemplateId
		? (JSM_TEMPLATE_ID_TO_BLUEPRINT_KEY[selectedTemplateId] ?? selectedTemplateId)
		: undefined;
	const progressDurationMs = isReliableProvisioningEnabled
		? JSM_USE_CASE_PROVISIONING_PROGRESS_DURATION_MS
		: LEGACY_JSM_USE_CASE_PROVISIONING_PROGRESS_DURATION_MS;
	const progress = useJsmUseCaseProvisioningProgress({
		durationMs: progressDurationMs,
		isComplete,
		isErrored: hasError,
	});

	const dataFoundationAttributes = useMemo(
		() => ({
			selectedTeamType,
			templateKey,
			isJSMOnboarding: true,
			isServiceCollectionEnabled: true,
		}),
		[selectedTeamType, templateKey],
	);

	const hasFiredErrorEventRef = useRef(false);
	useEffect(() => {
		if (hasProjectError && !hasFiredErrorEventRef.current) {
			hasFiredErrorEventRef.current = true;
			createAnalyticsEvent({
				eventType: 'operational',
				actionSubject: 'jsmUseCaseProjectCreate',
				action: 'failed',
				attributes: {
					...(isReliableProvisioningEnabled
						? {
								apiErrorMessage: projectFailure?.apiErrorMessage,
								backendTraceId: projectFailure?.backendTraceId,
								attemptId: projectLifecycle?.attemptId,
								elapsedTimeMs: projectLifecycle?.elapsedTimeMs,
								errorCategory: projectLifecycle?.errorCategory,
								failurePhase: projectLifecycle?.failurePhase,
								httpStatus: projectFailure?.httpStatus,
								projectKey,
								teamType: selectedTeamType,
								templateKey,
								viewInstanceId: projectLifecycle?.viewInstanceId,
							}
						: {}),
					errorMessage: projectErrorMessage,
					errorType: projectStatus,
					isJSMOnboarding: true,
					isServiceCollectionEnabled: true,
					selectedTemplateId,
				},
			}).fire();
		}
	}, [
		createAnalyticsEvent,
		hasProjectError,
		isReliableProvisioningEnabled,
		projectErrorMessage,
		projectFailure,
		projectKey,
		projectLifecycle,
		projectStatus,
		selectedTeamType,
		selectedTemplateId,
		templateKey,
	]);

	const handleRenderError = useCallback(() => {
		createAnalyticsEvent({
			eventType: 'ui',
			actionSubject: 'jsmUseCaseProvisioningScreen',
			action: 'failed',
			attributes: {
				isJSMOnboarding: true,
				isServiceCollectionEnabled: true,
			},
		}).fire();
	}, [createAnalyticsEvent]);

	useEffect(() => {
		if (!isComplete || !projectKey || !onDemandUrl) {
			return undefined;
		}

		const timeoutId = window.setTimeout(() => {
			const redirectUrl = createJsmUseCaseProductLandingUrl({
				cloudId,
				createAnalyticsEvent,
				isCsmAppificationEnabled,
				isProduction,
				isIncidentManagement,
				onDemandUrl,
				projectKey,
				templateId: selectedTemplateId,
			});
			if (!hasFiredRedirectAnalyticsRef.current) {
				createAnalyticsEvent({
					eventName: 'redirectedToProduct',
					eventComponent: 'confirmationPage',
					event: 'redirected',
					eventType: 'ui',
					action: 'redirected',
					attributes: {
						reactComponent: 'JsmUseCaseProvisioningScreen',
						selectedTeamType,
						templateKey,
						projectKey,
						isJSMOnboarding: true,
						isServiceCollectionEnabled: true,
					},
				}).fire();
				hasFiredRedirectAnalyticsRef.current = true;
			}
			completeProductSetupSystem('service-collection-provisioning-screen');
			window.location.assign(redirectUrl);
		}, SUCCESS_REDIRECT_DELAY_MS);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [
		cloudId,
		completeProductSetupSystem,
		createAnalyticsEvent,
		isComplete,
		isIncidentManagement,
		isCsmAppificationEnabled,
		isProduction,
		onDemandUrl,
		projectKey,
		selectedTeamType,
		selectedTemplateId,
		templateKey,
	]);

	const progressStatus: JsmUseCaseProvisioningProgressStatus = hasError
		? 'error'
		: isComplete
			? 'success'
			: 'neutral';
	const incidentFeedback = getJsmIncidentManagementFeedback({
		isIncidentManagement,
		projectStatus,
		incidentStatus: incidentSetup.status,
		failure: incidentSetup.failure,
	});
	const statusMessage = intl.formatMessage(
		getJsmUseCaseProvisioningStatusMessage({
			hasError,
			isComplete,
			progress,
			incidentMessage: incidentFeedback.message,
		}),
	);
	const getRecoveryUrl = () => {
		if (
			!incidentFeedback.canRecover ||
			!projectKey ||
			!onDemandUrl ||
			!isValidOnDemandUrl(onDemandUrl)
		) {
			return undefined;
		}
		// The product URL builder navigates when given an invalid site name.
		return createJsmUseCaseProductLandingUrl({
			cloudId,
			createAnalyticsEvent,
			isCsmAppificationEnabled,
			isProduction,
			onDemandUrl,
			projectKey,
			templateId: selectedTemplateId,
		});
	};

	return (
		<ErrorBoundary onError={handleRenderError}>
			<DataFoundationsWrapper
				dataFoundationsEventOption="ProvisioningScreenViewed"
				attributes={dataFoundationAttributes}
				fireOncePerMount={isReliableProvisioningEnabled}
			>
				<JSMOverlayTemplate
					size="md"
					showBrandFooter={false}
					hideLogo
					renderRawContent
					isJsmCrossflow
				>
					<JsmUseCaseProvisioningScreenContent
						disableAnimation={!shouldAnimateArtwork}
						hasError={hasError}
						progress={progress}
						progressLabel={intl.formatMessage(jsmUseCaseProvisioningMessages.progressLabel)}
						progressStatus={progressStatus}
						statusMessage={statusMessage}
						isBusy={!isComplete && !hasError}
						isIncidentManagement={isIncidentManagement}
						recoveryUrl={getRecoveryUrl()}
						onRecoveryClick={() => {
							createAnalyticsEvent({
								eventType: 'ui',
								action: 'clicked',
								actionSubject: 'link',
								actionSubjectId: 'jsmIncidentOpenServiceSpace',
							}).fire();
						}}
					/>
				</JSMOverlayTemplate>
			</DataFoundationsWrapper>
		</ErrorBoundary>
	);
};

export const JsmUseCaseProvisioningScreenContent = ({
	disableAnimation,
	hasError,
	isBusy,
	progress,
	progressLabel,
	progressStatus,
	statusMessage,
	isIncidentManagement = false,
	recoveryUrl,
	onRecoveryClick,
}: {
	readonly disableAnimation: boolean;
	readonly hasError: boolean;
	readonly isBusy: boolean;
	readonly progress: number;
	readonly progressLabel: string;
	readonly progressStatus: JsmUseCaseProvisioningProgressStatus;
	readonly statusMessage: string;
	readonly isIncidentManagement?: boolean;
	readonly recoveryUrl?: string;
	readonly onRecoveryClick?: () => void;
}) => (
	<>
		{isIncidentManagement && <VisuallyHidden role="status">{statusMessage}</VisuallyHidden>}
		<section css={screenStyles} aria-busy={isBusy}>
			<div css={contentPanelStyles}>
				<div css={brandRowStyles}>
					<span css={brandLogoWrapperStyles}>
						<ServiceCollectionLogoV2 size="small" />
					</span>
					<div css={brandDividerStyles} />
					<div css={includesWrapperStyles}>
						<span css={includesTextStyles}>
							<FormattedMessage {...jsmUseCaseProvisioningMessages.includes} />
						</span>
						<div css={productMarksStyles} aria-hidden="true">
							<SmallProductMark>
								<JiraServiceManagementIcon size="xsmall" label="" />
							</SmallProductMark>
							<SmallProductMark>
								<CustomerServiceManagementIcon size="xsmall" label="" />
							</SmallProductMark>
							<SmallProductMark>
								<RovoIcon size="xsmall" appearance="neutral" label="" />
							</SmallProductMark>
						</div>
					</div>
				</div>

				<h1 css={titleStyles}>
					<FormattedMessage {...jsmUseCaseProvisioningMessages.title} />
				</h1>
				<p
					css={[
						statusStyles,
						isIncidentManagement && incidentStatusStyles,
						hasError && errorStatusStyles,
					]}
				>
					{statusMessage}
				</p>
				<JsmUseCaseProvisioningProgressBar
					label={progressLabel}
					progress={progress}
					status={progressStatus}
				/>
				{recoveryUrl && (
					<Link href={recoveryUrl} onClick={onRecoveryClick}>
						<FormattedMessage {...jsmUseCaseProvisioningMessages.openServiceSpace} />
					</Link>
				)}
			</div>
			<div css={artworkPanelStyles}>
				<JsmUseCaseProvisioningArtwork disableAnimation={disableAnimation} />
			</div>
		</section>
	</>
);

export const getJsmUseCaseProvisioningStatusMessage = ({
	hasError,
	isComplete,
	progress,
	incidentMessage,
}: {
	readonly hasError: boolean;
	readonly isComplete: boolean;
	readonly progress: number;
	readonly incidentMessage?: MessageDescriptor;
}): MessageDescriptor => {
	if (incidentMessage) return incidentMessage;
	if (hasError) {
		return jsmUseCaseProvisioningMessages.errorStatus;
	}

	if (isComplete) {
		return jsmUseCaseProvisioningMessages.doneStatus;
	}

	if (progress >= JSM_USE_CASE_PROVISIONING_MAX_PENDING_PROGRESS) {
		return jsmUseCaseProvisioningMessages.almostThereStatus;
	}

	if (progress < PROGRESS_MESSAGE_STEP_SIZE) {
		return jsmUseCaseProvisioningMessages.settingUpSpaceStatus;
	}

	if (progress < PROGRESS_MESSAGE_STEP_SIZE * 2) {
		return jsmUseCaseProvisioningMessages.addingRequestTypesStatus;
	}

	if (progress < PROGRESS_MESSAGE_STEP_SIZE * 3) {
		return jsmUseCaseProvisioningMessages.settingUpHelpCenterStatus;
	}

	return jsmUseCaseProvisioningMessages.settingUpPortalStatus;
};

export const createJsmUseCaseProductLandingUrl = ({
	cloudId,
	createAnalyticsEvent,
	isCsmAppificationEnabled,
	isProduction,
	isIncidentManagement = false,
	onDemandUrl,
	projectKey,
	templateId,
}: {
	readonly cloudId: string;
	readonly createAnalyticsEvent: CreateUIAnalyticsEvent;
	readonly isCsmAppificationEnabled: boolean;
	readonly isProduction: boolean;
	readonly isIncidentManagement?: boolean;
	readonly onDemandUrl: string;
	readonly projectKey: string;
	readonly templateId: string | undefined;
}) => {
	const productUrl = createProductUrl({
		admin: false,
		cloudId,
		createAnalyticsEvent,
		dataRegion: undefined,
		isProduction,
		onDemandUrl,
		primaryCollectionProduct: ProductKey.JIRA_SERVICE_MANAGEMENT,
		products: [ProductKey.JIRA_SERVICE_MANAGEMENT],
		projectKey,
		templateChoice: '',
		templateId,
		useAppifiedCsmRoute: isCsmAppificationEnabled,
	});
	let destinationUrl = productUrl;
	if (isIncidentManagement) {
		try {
			const experiment = getExperiment(experiments.operationsLandFlow);
			if (experiment.type === 'enrolled' && experiment.value === true) {
				destinationUrl = new URL('/jira/ops/overview?src=signup', productUrl).toString();
			}
		} catch {
			// Keep the project destination if the experiment becomes unavailable before navigation.
		}
	}

	return `${getIdentityBaseUrl()}/login?${new URLSearchParams({
		continue: destinationUrl,
		application: 'jira',
	})}`;
};

const SmallProductMark = ({ children }: PropsWithChildren) => (
	<span css={smallProductMarkStyles}>{children}</span>
);

const JsmUseCaseProvisioningArtwork = ({
	disableAnimation,
}: {
	readonly disableAnimation: boolean;
}) => (
	<div
		css={artworkContainerStyles}
		data-testid="jsm-use-case-provisioning-artwork"
		data-reduced-motion={disableAnimation}
		aria-hidden="true"
	>
		<JsmUseCaseProvisioningGrid />
		<ServiceCollectionProvisioningAppIcons disableAnimation={disableAnimation} />
	</div>
);

const usePrefersReducedMotion = () => {
	const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

	useEffect(() => {
		const mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');

		if (!mediaQuery) {
			return undefined;
		}

		setPrefersReducedMotion(mediaQuery.matches);

		const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);

		mediaQuery.addEventListener?.('change', handleChange);

		return () => {
			mediaQuery.removeEventListener?.('change', handleChange);
		};
	}, []);

	return prefersReducedMotion;
};

const screenStyles = css({
	display: 'flex',
	minHeight: '568px',
	overflow: 'hidden',
	color: token('color.text'),
	backgroundColor: token('elevation.surface'),
	[media.below.sm]: {
		flexDirection: 'column',
	},
});

const contentPanelStyles = css({
	boxSizing: 'border-box',
	display: 'flex',
	flexDirection: 'column',
	width: '50%',
	paddingBlockStart: token('space.600'),
	paddingBlockEnd: token('space.600'),
	paddingInlineStart: token('space.600'),
	paddingInlineEnd: token('space.600'),
	[media.below.sm]: {
		width: '100%',
	},
});

const artworkPanelStyles = css({
	position: 'relative',
	display: 'grid',
	placeItems: 'center',
	width: '50%',
	backgroundColor: wacToken('color.extended.saffron100'),
	clipPath: 'polygon(9% 0, 100% 0, 100% 100%, 0 100%)',
	[media.below.sm]: {
		width: '100%',
		minHeight: '320px',
		clipPath: 'none',
	},
});

const artworkContainerStyles = css({
	display: 'grid',
	placeItems: 'center',
	width: '100%',
	height: '100%',
});

const brandRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	columnGap: token('space.150'),
	marginBlockEnd: token('space.500'),
});

const brandLogoWrapperStyles = css({
	display: 'inline-flex',
	flexShrink: 0,
	width: '144px',
	overflow: 'hidden',
});

const brandDividerStyles = css({
	width: token('border.width', '1px'),
	height: token('space.400'),
	backgroundColor: token('color.border'),
});

const includesWrapperStyles = css({
	display: 'flex',
	alignItems: 'center',
	columnGap: token('space.100'),
});

const includesTextStyles = css({
	color: token('color.text.subtle'),
	font: token('font.body.small'),
});

const productMarksStyles = css({
	display: 'flex',
	columnGap: token('space.050'),
});

const smallProductMarkStyles = css({
	display: 'inline-flex',
	flexShrink: 0,
	width: token('space.250'),
	height: token('space.250'),
	overflow: 'hidden',
});

const titleStyles = css({
	marginBlockStart: token('space.0'),
	marginBlockEnd: token('space.0'),
	color: token('color.text'),
	font: token('font.heading.large'),
});

const statusStyles = css({
	display: 'flex',
	alignItems: 'flex-end',
	blockSize: token('space.600'),
	marginBlockStart: token('space.0'),
	marginBlockEnd: token('space.0'),
	color: token('color.text.subtle'),
	font: token('font.body'),
});

const errorStatusStyles = css({
	color: token('color.text.danger'),
});

const incidentStatusStyles = css({
	blockSize: 'auto',
	minBlockSize: token('space.600'),
	paddingBlockStart: token('space.200'),
});
